import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// features
import { socketUserDocumentGeneratedReceivedSuccess } from '../../sockets-v2/actions';
import { updateLoadDriverInvoiceSuccess } from '../../dispatch-details-new/load/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature payroll
import * as A from './actions';
import { setInitialState } from '../actions';
import { getPayrollStatisticListAndCurrency } from '../helpers';
//////////////////////////////////////////////////

const initialState = {
  totalCount: 0,
  visited: false,
  loading: false,
  itemList: null,
  filterParams: {},
  usedReport: null,
  titleSortValues: {},
  reportPending: false,
  updatedInvoice: null,
  tableTitleFilters: {},
  availableReports: null,
  recallDocumentsGuid: null,
  currentBranchConfigs: null,
  pagination: { limit: 20, offset: 0 },
};

const setVendorInitialState = () => initialState;

const setVisited = (state: Object, data: boolean=true) => (
  P.$set('visited', data, state)
);

const setReportPending = (state: Object) => (
  P.$set('reportPending', true, state)
);

const setReports = (state: Object, data: Array) => (
  P.$set('availableReports', data, state)
);

const setUsedReport = (state: Object, data: Object) => (
  P.$all(
    P.$set('itemList', null),
    P.$set('usedReport', data),
    P.$set('titleSortValues', {}),
    P.$set('reportPending', false),
    P.$set('tableTitleFilters', {}),
    P.$set('pagination', initialState.pagination),
    state,
  )
);

const cleanQuickFilter = (state: Object) => (
  P.$all(
    P.$set('itemList', null),
    P.$set('filterParams', {}),
    P.$set('pagination', initialState.pagination),
    state,
  )
);

const setQuickFilterParams = (state: Object, data: Object) => (
  P.$set('filterParams', data, state)
);

const setListLoading = (state: Object, data: boolean) => (
  P.$set('loading', data, state)
);

const resetListAndPagination = (state: Object) => (
  P.$all(
    P.$set('itemList', null),
    P.$set('pagination', initialState.pagination),
    state,
  )
);

const getItemListSuccess = (state: Object, data: Object) => {
  const { itemList, pagination } = state;
  const newOffset = R.add(pagination.offset, pagination.limit);
  const indexAdditional = G.ifElse(
    R.isNil(itemList),
    0,
    R.length(R.values(itemList)),
  );
  const newPayrolls = data.results.map((p: Object, i: number) => R.mergeRight(
    p,
    {
      selected: false,
      expanded: false,
      index: R.add(i, indexAdditional),
    },
  ));
  const newList = R.mergeRight(itemList, R.indexBy(R.prop(GC.FIELD_GUID), newPayrolls));
  return P.$all(
    P.$set('itemList', newList),
    P.$set('totalCount', data.totalCount),
    P.$set(
      'pagination.offset',
      G.ifElse(
        R.gt(data.totalCount, newOffset),
        newOffset,
        data.totalCount,
      ),
    ),
    state,
  );
};

const selectItem = (state: Object, data: string) => {
  if (R.equals(data, 'all')) {
    const { itemList } = state;
    const value = R.not(R.all(
      (item: Object) => item.selected,
      R.values(itemList),
    ));
    return P.$set(
      'itemList',
      R.map(
        (item: Object) => R.assoc('selected', value, item),
        itemList,
      ),
      state,
    );
  }
  return P.$toggle(`itemList.${data}.selected`, state);
};

const togglePayrollDetails = (state: Object, data: string) => (
  P.$toggle(`itemList.${data}.expanded`, state)
);

const setPayrollDetailsLoading = (state: Object, data: string) => (
  P.$set(`itemList.${data}.detailsLoading`, true, state)
);

const getPayrollDetailsSuccess = (state: Object, data: Object) => (
  P.$set(
    `itemList.${data.guid}`,
    R.mergeRight(
      R.path(['itemList', data.guid], state),
      {
        detailsError: false,
        details: data.details,
        detailsLoading: false,
      },
    ),
    state,
  )
);

const setPayrollStatisticLoading = (state: Object, { guid, loading }: Object) => (
  P.$set(`itemList.${guid}.statisticLoading`, loading, state)
);

const getPayrollStatisticSuccess = (state: Object, { guid, statistic }: Object) => {
  const { statisticList, statisticCurrency } = getPayrollStatisticListAndCurrency(statistic);

  return P.$all(
    P.$set(`itemList.${guid}.statistic`, statisticList),
    P.$set(`itemList.${guid}.statisticCurrency`, statisticCurrency),
    state,
  );
};

const getPayrollDetailsError = (state: Object, data: string) => (
  P.$all(
    P.$set(`itemList.${data}.detailsError`, true),
    P.$set(`itemList.${data}.detailsLoading`, false),
    state,
  )
);

const collapseAndResetPayrollInvoices = (state: Object, data: string) => (
  P.$set(
    `itemList.${data.guid}`,
    R.mergeRight(
      R.path(['itemList', data.guid], state),
      {
        ...data,
        details: null,
        statistic: null,
        expanded: false,
      },
    ),
    state,
  )
);

const removePayrollInvoiceFromUI = (state: Object, data: Object) => {
  const guid = R.path(['payroll', 'guid'], data);
  const item = R.path(['itemList', guid], state);
  return P.$set(
    `itemList.${guid}`,
    R.assoc(
      'details',
      R.filter(
        (invoice: Object) => G.notEquals(invoice.guid, data.invoiceGuid),
        item.details,
      ),
      R.mergeRight(item, data.payroll),
    ),
    state,
  );
};

const removeVendorPayrollFromUI = (state: Object, data: string) => (
  P.$all(
    P.$drop(`itemList.${data}`),
    P.$set('totalCount', R.dec(state.totalCount)),
    state,
  )
);

const updateVendorInvoiceSuccess = (state: Object, data: string) => {
  const { invoice, updateFrom } = data;

  if (R.or(
    G.isNilOrEmpty(R.path(['itemList'], state)),
    R.pathEq(GC.INVOICE_TYPE_FLEET_INVOICE, [GC.FIELD_TYPE], invoice),
  )) return state;

  const mappedInvoice = G.mapDropdownsObjectInEntity(
    GC.FIELD_DISPLAYED_VALUE,
    [GC.FIELD_INVOICE_STATUS, GC.FIELD_MODE],
    invoice,
  );

  if (R.equals(updateFrom, 'addPayroll')) {
    return P.$set('updatedInvoice', mappedInvoice, state);
  }

  const payrollGuid = R.prop(GC.FIELD_FLEET_VENDOR_PAYROLL_GUID, invoice);
  const invoiceGuid = R.prop(GC.FIELD_GUID, invoice);
  const grandTotal = R.path(['payrollTotals', 'grandTotal'], invoice);
  const invoicesTotal = R.path(['payrollTotals', 'invoicesTotal'], invoice);
  const previousDetails = R.path(['itemList', payrollGuid, 'details'], state);

  let newPayroll = {
    ...R.path(['itemList', payrollGuid], state),
    grandTotal,
    invoicesTotal,
  };

  if (G.isNotNilAndNotEmpty(previousDetails)) {
    const details = R.map((item: Object) => {
      if (R.equals(G.getGuidFromObject(item), invoiceGuid)) {
        return R.assoc(
          GC.FIELD_TEL_PRIMARY_REFERENCE_VALUE,
          R.prop(GC.FIELD_TEL_PRIMARY_REFERENCE_VALUE, item),
          mappedInvoice,
        );
      }

      return item;
    }, previousDetails);

    newPayroll = {
      ...newPayroll,
      details,
    };
  }

  return P.$set(`itemList.${payrollGuid}`, newPayroll, state);
};

const onSocketUserDocumentGeneratedReceivedSuccess = (state: Object) => (
  P.$set('recallDocumentsGuid', G.generateGuid(), state)
);

export default createReducer({
  [A.selectItem]: selectItem,
  [A.setReports]: setReports,
  [A.setVisited]: setVisited,
  [A.setUsedReport]: setUsedReport,
  [A.setListLoading]: setListLoading,
  [A.setReportPending]: setReportPending,
  [A.cleanQuickFilter]: cleanQuickFilter,
  [setInitialState]: setVendorInitialState,
  [A.setTableTitleSort]: G.setTableTitleSort,
  [A.getItemListSuccess]: getItemListSuccess,
  [A.setTableTitleFilter]: G.setTableTitleFilter,
  [A.togglePayrollDetails]: togglePayrollDetails,
  [A.setQuickFilterParams]: setQuickFilterParams,
  [A.resetListAndPagination]: resetListAndPagination,
  [A.getPayrollDetailsError]: getPayrollDetailsError,
  [A.setPayrollDetailsLoading]: setPayrollDetailsLoading,
  [A.getPayrollDetailsSuccess]: getPayrollDetailsSuccess,
  [A.removeVendorPayrollFromUI]: removeVendorPayrollFromUI,
  [A.setPayrollStatisticLoading]: setPayrollStatisticLoading,
  [A.getPayrollStatisticSuccess]: getPayrollStatisticSuccess,
  [A.updateVendorInvoiceSuccess]: updateVendorInvoiceSuccess,
  [A.removePayrollInvoiceFromUI]: removePayrollInvoiceFromUI,
  [A.collapseAndResetPayrollInvoices]: collapseAndResetPayrollInvoices,
  //
  [updateLoadDriverInvoiceSuccess]: updateVendorInvoiceSuccess,
  [socketUserDocumentGeneratedReceivedSuccess]: onSocketUserDocumentGeneratedReceivedSuccess,
}, initialState);
